import { Platform } from 'react-native';

export type ScheduleIosUiPresentOptions = {
  /**
   * When false, present once on the next animation frame only.
   * Use for ActionSheetIOS — repeating `showActionSheetWithOptions` stacks duplicate sheets.
   * Default true keeps retry behavior for RN Modal `visible` toggles that can fail silently.
   */
  retries?: boolean;
};

/**
 * Present iOS UI (modals, action sheets) without waiting on InteractionManager.
 * Long-running interactions (maps, Firestore, keyboard) can stall InteractionManager and leave
 * pickers silent until the app backgrounds — the "fake app switch" workaround users report.
 *
 * With retries (default): fires immediately, on the next animation frame, then once more after
 * a short delay so at least one attempt lands after the current commit cycle.
 */
export function scheduleIosUiPresent(
  onPresent: () => void,
  options?: ScheduleIosUiPresentOptions,
): () => void {
  if (Platform.OS !== 'ios') {
    onPresent();
    return () => {};
  }

  let cancelled = false;
  const retries = options?.retries !== false;

  if (!retries) {
    const rafId = requestAnimationFrame(() => {
      if (!cancelled) onPresent();
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }

  onPresent();

  const rafId = requestAnimationFrame(() => {
    if (!cancelled) onPresent();
  });

  const timeoutId = setTimeout(() => {
    if (!cancelled) onPresent();
  }, 80);

  return () => {
    cancelled = true;
    cancelAnimationFrame(rafId);
    clearTimeout(timeoutId);
  };
}
